import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import AppShell from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Sparkles, Calendar, ShieldCheck, CheckCircle2, Loader2, XCircle, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useSubscription, daysUntil } from "@/context/SubscriptionContext";

const STATUS_LABEL = {
  free: "Free plan",
  trialing: "In free trial",
  trial_expired: "Trial expired",
  active: "Active",
  past_due: "Past due",
  canceled: "Canceled",
};

export default function Billing() {
  const { subscription, plan, is_pro, effective_status, refresh, cancel, pollCheckout } = useSubscription();
  const [busy, setBusy] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  // If Stripe redirected here (Billing/Success), poll status.
  useEffect(() => {
    const sid = searchParams.get("session_id");
    if (!sid) return;
    let attempts = 0;
    const maxAttempts = 8;
    const interval = 2500;
    let cancelled = false;

    const tick = async () => {
      if (cancelled) return;
      attempts += 1;
      try {
        const data = await pollCheckout(sid);
        if (data.payment_status === "paid") {
          toast.success("Payment received — Pro is now active!");
          await refresh();
          searchParams.delete("session_id");
          setSearchParams(searchParams, { replace: true });
          return;
        }
        if (data.session_status === "expired") {
          toast.error("The checkout session expired. Please retry.");
          return;
        }
      } catch (err) {
        // Transient network errors during polling are expected; log and retry.
        console.warn("[billing] Checkout poll failed (attempt " + attempts + "):", err?.message || err);
      }
      if (attempts < maxAttempts) setTimeout(tick, interval);
      else toast.info("Payment is taking longer than usual. Refresh in a moment.");
    };
    tick();
    return () => { cancelled = true; };
  }, []);

  const onCancel = async () => {
    setBusy(true);
    try {
      const res = await cancel();
      toast.success(res.message);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const status = effective_status;
  const trialLeft = subscription?.trial_ends_at ? daysUntil(subscription.trial_ends_at) : null;
  const periodEndsIn = subscription?.current_period_end ? daysUntil(subscription.current_period_end) : null;

  return (
    <AppShell pageTitle="Billing" pageDescription="Manage your Arogya Pramana Pro subscription.">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Current plan */}
        <div className="lg:col-span-2 rounded-lg border border-border bg-white p-6" data-testid="current-plan-card">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="overline">Current plan</div>
            <StatusPill status={status} />
          </div>
          <h2 className="mt-2 font-serif text-3xl text-primary">
            {plan ? plan.name : "Free plan"}
          </h2>
          <p className="text-sm text-muted-foreground mt-1">
            {plan ? `₹${plan.price_inr.toLocaleString("en-IN")} / month · billed in INR` : "You have access to the basic dashboard. Upgrade to unlock Pro features."}
          </p>

          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
            {status === "trialing" && (
              <InfoRow icon={Calendar} label="Trial ends" value={`in ${trialLeft} day${trialLeft === 1 ? "" : "s"} · ${formatDate(subscription.trial_ends_at)}`} testid="billing-trial-ends" />
            )}
            {status === "active" && (
              <>
                <InfoRow icon={Calendar} label="Renews on" value={`${formatDate(subscription.current_period_end)} (in ${periodEndsIn} day${periodEndsIn === 1 ? "" : "s"})`} testid="billing-renews-on" />
                {subscription.cancel_at_period_end && (
                  <InfoRow icon={XCircle} label="Cancels at period end" value="Will not renew — you keep Pro until the date above." tone="warning" testid="billing-cancel-scheduled" />
                )}
              </>
            )}
            {(status === "trial_expired" || status === "past_due") && (
              <InfoRow icon={XCircle} label="Access ended" value="Pro features are locked until you re-subscribe." tone="destructive" />
            )}
          </div>

          <div className="mt-6 flex items-center gap-3 flex-wrap">
            {status === "free" && (
              <Link to="/upgrade" className="rounded-md bg-primary text-primary-foreground text-sm font-medium px-5 py-2.5" data-testid="billing-upgrade-cta">
                Upgrade to Pro <ArrowRight size={14} className="inline ml-1" />
              </Link>
            )}
            {(status === "trialing" || status === "trial_expired" || status === "past_due") && (
              <Link to="/upgrade" className="rounded-md bg-primary text-primary-foreground text-sm font-medium px-5 py-2.5" data-testid="billing-subscribe-cta">
                {status === "trialing" ? "Subscribe now" : "Renew Pro"}
              </Link>
            )}
            {status === "active" && !subscription?.cancel_at_period_end && (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="outline" data-testid="billing-cancel-button">
                    Cancel renewal
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle className="font-serif text-2xl">Cancel auto-renewal?</AlertDialogTitle>
                    <AlertDialogDescription>
                      You will keep Pro access until <strong>{formatDate(subscription.current_period_end)}</strong>. After that, your account moves to Free.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Keep my plan</AlertDialogCancel>
                    <AlertDialogAction onClick={onCancel} disabled={busy} data-testid="billing-cancel-confirm">
                      Yes, cancel renewal
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
          </div>
        </div>

        {/* Pro perks card */}
        <div className={`rounded-lg border ${is_pro ? "bg-primary text-primary-foreground border-primary" : "bg-white border-border"} p-6`} data-testid="pro-perks-card">
          <div className="flex items-center gap-2">
            <Sparkles size={16} />
            <div className={`overline ${is_pro ? "text-primary-foreground/70" : ""}`}>Pro benefits</div>
          </div>
          <ul className={`mt-4 space-y-2 text-sm ${is_pro ? "text-primary-foreground/95" : "text-foreground"}`}>
            {(plan?.features || []).map((f) => (
              <li key={f} className="flex items-start gap-2">
                <CheckCircle2 size={14} className="mt-0.5 shrink-0" /> <span>{f}</span>
              </li>
            ))}
            {!plan && (
              <>
                <li className="flex items-start gap-2"><Sparkles size={14} className="mt-0.5" /> Verified Pro badge on your profile</li>
                <li className="flex items-start gap-2"><Sparkles size={14} className="mt-0.5" /> Analytics dashboards</li>
                <li className="flex items-start gap-2"><Sparkles size={14} className="mt-0.5" /> Priority DPDP support</li>
              </>
            )}
          </ul>
        </div>
      </div>

      {/* Trust footer */}
      <div className="mt-6 rounded-lg border border-border bg-muted/40 p-5 text-xs text-muted-foreground flex items-start gap-3">
        <ShieldCheck size={14} className="text-primary mt-0.5 shrink-0" />
        <div>
          Payments are processed by <strong>Stripe</strong> — Arogya Pramana never sees your card details. We store only your subscription plan and status.
          You can cancel at any time and continue to enjoy Pro until the end of your current billing period.
        </div>
      </div>
    </AppShell>
  );
}

function StatusPill({ status }) {
  const map = {
    free: "bg-muted text-primary",
    trialing: "bg-accent/15 text-accent",
    active: "bg-primary/10 text-primary",
    trial_expired: "bg-destructive/10 text-destructive",
    past_due: "bg-destructive/10 text-destructive",
    canceled: "bg-muted text-muted-foreground",
  };
  return (
    <span
      className={`text-[10px] font-semibold uppercase tracking-widest rounded-full px-2 py-0.5 ${map[status] || map.free}`}
      data-testid="billing-status-pill"
    >
      {STATUS_LABEL[status] || "Free"}
    </span>
  );
}

function InfoRow({ icon: Icon, label, value, tone, testid }) {
  const toneClass = tone === "destructive"
    ? "text-destructive"
    : tone === "warning" ? "text-accent" : "text-primary";
  return (
    <div className="rounded-md border border-border p-3" data-testid={testid}>
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-muted-foreground">
        <Icon size={12} /> {label}
      </div>
      <div className={`mt-1 text-sm font-medium ${toneClass}`}>{value}</div>
    </div>
  );
}

function formatDate(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
}
